import type { FieldDefinition } from '@/types/calculator'

/**
 * Serialise calculator inputs to URL query params, keyed by field name.
 * Values that are undefined, null or empty strings are skipped.
 */
export function encodeInputs(
  fields: FieldDefinition[],
  inputs: Record<string, unknown>
): Record<string, string> {
  const params: Record<string, string> = {}
  for (const field of fields) {
    const value = inputs[field.name]
    if (value === undefined || value === null || value === '') continue
    params[field.name] = String(value)
  }
  return params
}

/**
 * Parse query params back into typed inputs using each field's type.
 * Unknown keys and values that fail validation for the field type are skipped.
 */
export function decodeInputs(
  fields: FieldDefinition[],
  params: URLSearchParams
): Record<string, unknown> {
  const decoded: Record<string, unknown> = {}
  for (const field of fields) {
    const raw = params.get(field.name)
    if (raw === null) continue
    switch (field.type) {
      case 'number':
      case 'money':
      case 'percentage': {
        const num = Number(raw)
        if (Number.isFinite(num)) decoded[field.name] = num
        break
      }
      case 'checkbox': {
        if (raw === 'true' || raw === 'false') decoded[field.name] = raw === 'true'
        break
      }
      case 'select':
      case 'radio': {
        if (field.options?.some((o) => o.value === raw)) decoded[field.name] = raw
        break
      }
      case 'text': {
        decoded[field.name] = raw
        break
      }
    }
  }
  return decoded
}
